<script setup lang="ts">
import {
    BookOpen,
    CheckCircle2,
    FileUp,
    Info,
    Loader2,
    Trash2,
    XCircle
} from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import ConfirmDialog from '@/components/ConfirmDialog.vue';
import SkillCompatibilityNotice from '@/components/settingsMenu/sections/SkillCompatibilityNotice.vue';
import Button from '@/components/ui/button/Button.vue';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toast';
import { formatDate } from '@/lib/commonFunctions';
import {
    type AgentSkill,
    deleteAgentSkill,
    getAgentSkills,
    importAgentSkills,
    type ImportReport,
    setAgentSkillExcluded
} from '@/services/agent_skills';
import { useAgentSkillsStore } from '@/stores/agentSkillsStore';
import { useTenantStore } from '@/stores/tenant';

// Matches MAX_SKILLS_PER_TENANT.
const MAX_SKILLS = 50;

const toast = useToast();
const tenantStore = useTenantStore();
const agentSkillsStore = useAgentSkillsStore();

const skills = ref<AgentSkill[]>([]);
const isLoading = ref(false);
const isImporting = ref(false);
const busySlugs = ref(new Set<string>());
const report = ref<ImportReport | null>(null);
const picked = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const isReplaceDialogOpen = ref(false);
const isDeleteDialogOpen = ref(false);
const skillToDelete = ref<AgentSkill | null>(null);
let reportTimer: ReturnType<typeof setTimeout> | undefined;

const isAdmin = computed(() => tenantStore.tenantAdmin);
const tenantId = computed(() => tenantStore.tenantId || '');
const quotaUsed = computed(() => (skills.value.length / MAX_SKILLS) * 100);
const conflictName = computed(() => report.value?.conflicts[0]?.slug ?? '');

async function loadSkills() {
    if (!tenantId.value) {
        skills.value = [];
        return;
    }
    isLoading.value = true;
    try {
        skills.value = await getAgentSkills(tenantId.value);
        // The composer caches its own copy per tenant: without this, `/name` keeps offering the
        // list from before this import, toggle or delete.
        await agentSkillsStore.load(tenantId.value, true);
    } catch (error) {
        console.error(error);
        toast.toast({
            title: 'Could not load skills',
            description: 'Please try again later.',
            variant: 'destructive'
        });
    } finally {
        isLoading.value = false;
    }
}

async function onFilePicked(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Cleared now, or picking the same file twice fires no change event.
    input.value = '';
    if (!file || !tenantId.value) return;

    // Kept for a confirmed replacement: the input no longer holds it.
    picked.value = file;
    await send(file, false);
}

async function send(file: File, overwrite: boolean) {
    isImporting.value = true;
    report.value = null;
    try {
        report.value = await importAgentSkills(
            tenantId.value,
            [file],
            overwrite
        );
        isReplaceDialogOpen.value = report.value.conflicts.length > 0;
        clearTimeout(reportTimer);
        // Only what went well fades: a rejection has to be read, and conflicts feed the
        // replace dialog, which would lose the files to replace.
        if (!isReplaceDialogOpen.value && report.value.imported.length) {
            reportTimer = setTimeout(() => {
                if (report.value) report.value.imported = [];
            }, 4000);
        }
        await loadSkills();
    } catch (error) {
        // The one refusal the report does not carry: the import would pass the tenant's limit.
        const message = error instanceof Error ? error.message : undefined;
        toast.toast({
            title: 'Import failed',
            description: message ?? 'An unexpected error occurred.',
            variant: 'destructive'
        });
    } finally {
        isImporting.value = false;
    }
}

async function replaceConflict() {
    if (picked.value) await send(picked.value, true);
}

async function toggle(skill: AgentSkill, enabled: boolean) {
    busySlugs.value = new Set(busySlugs.value).add(skill.slug);
    try {
        await setAgentSkillExcluded(tenantId.value, skill.slug, !enabled);
        skill.excluded = !enabled;
        // Switching one off has to take it out of `/name` too.
        await agentSkillsStore.load(tenantId.value, true);
    } catch (error) {
        console.error(error);
        toast.toast({
            title: 'Could not change that',
            description: 'Please try again later.',
            variant: 'destructive'
        });
    } finally {
        const next = new Set(busySlugs.value);
        next.delete(skill.slug);
        busySlugs.value = next;
    }
}

function askRemove(skill: AgentSkill) {
    skillToDelete.value = skill;
    isDeleteDialogOpen.value = true;
}

async function remove() {
    const skill = skillToDelete.value;
    if (!skill) return;
    busySlugs.value = new Set(busySlugs.value).add(skill.slug);
    try {
        await deleteAgentSkill(tenantId.value, skill.slug);
        await loadSkills();
    } catch (error) {
        console.error(error);
        toast.toast({
            title: 'Could not remove that skill',
            description: 'Please try again later.',
            variant: 'destructive'
        });
    } finally {
        const next = new Set(busySlugs.value);
        next.delete(skill.slug);
        busySlugs.value = next;
    }
}

watch(() => [tenantStore.tenantId, tenantStore.tenantAdmin], loadSkills, {
    immediate: true
});
</script>

<template>
    <div class="flex flex-col gap-6">
        <div
            class="flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-start sm:justify-between"
        >
            <div class="flex flex-col gap-1.5">
                <h2 class="text-xl font-semibold">Skills</h2>
                <p class="max-w-xl text-sm text-muted-foreground">
                    Procedures your organisation imports once. The assistant
                    picks one up when a request matches its description, or when
                    you name it directly. Switch off the ones you do not want in
                    your own conversations.
                </p>
            </div>

            <div v-if="isAdmin" class="flex shrink-0 flex-col items-end gap-2">
                <input
                    ref="fileInput"
                    type="file"
                    accept=".md"
                    class="hidden"
                    @change="onFilePicked"
                />
                <Button
                    class="gap-2"
                    :disabled="isImporting"
                    @click="fileInput?.click()"
                >
                    <Loader2 v-if="isImporting" class="h-4 w-4 animate-spin" />
                    <FileUp v-else class="h-4 w-4" />
                    Import SKILL.md
                </Button>
                <div class="flex w-40 items-center gap-2">
                    <Progress
                        :model-value="quotaUsed"
                        class="h-1 flex-1 bg-border"
                    />
                    <span class="text-xs text-muted-foreground">
                        {{ skills.length }} of {{ MAX_SKILLS }}
                    </span>
                </div>
            </div>
        </div>

        <!-- No compatibility notice here: the list below carries it, on the same skills. -->
        <div
            v-if="report && (report.imported.length || report.rejected.length)"
            class="flex flex-col gap-2"
        >
            <div
                v-if="report.imported.length"
                class="flex gap-2.5 rounded-lg border border-green25 bg-green25/30 px-3 py-2.5 text-sm dark:bg-greenContrast25/10"
            >
                <CheckCircle2
                    class="mt-0.5 h-4 w-4 shrink-0 text-green100 dark:text-greenContrast25"
                />
                <ul class="space-y-1">
                    <li v-for="entry in report.imported" :key="entry.slug">
                        <span class="font-medium">{{ entry.filename }}</span>
                        <span class="text-muted-foreground">
                            — imported as <code>{{ entry.slug }}</code>
                            <template v-if="entry.replaced">
                                , replacing the previous version
                            </template>
                        </span>
                    </li>
                </ul>
            </div>

            <div
                v-for="entry in report.rejected"
                :key="entry.filename"
                class="flex gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm"
            >
                <XCircle class="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div>
                    <p class="font-medium">{{ entry.filename }}</p>
                    <ul class="mt-1 space-y-1 text-muted-foreground">
                        <li v-for="reason in entry.reasons" :key="reason">
                            {{ reason }}
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div
            v-if="isLoading"
            class="flex min-h-[24rem] w-full items-center justify-center text-muted-foreground"
        >
            <Loader2 class="h-5 w-5 animate-spin" />
        </div>

        <div
            v-else-if="!skills.length"
            class="flex flex-col items-center gap-4 py-20 text-center"
        >
            <div class="rounded-full bg-muted p-6">
                <BookOpen class="h-8 w-8" />
            </div>
            <h3 class="text-lg font-semibold">No skill yet</h3>
            <p class="max-w-md text-sm text-muted-foreground">
                <template v-if="isAdmin">
                    A skill is a markdown file opening with a name and a
                    description; import one to give the assistant a procedure to
                    follow.
                </template>
                <template v-else>
                    Your organisation has not imported any skill yet.
                </template>
            </p>
        </div>

        <div v-else class="flex flex-col divide-y divide-border">
            <div
                v-for="skill in skills"
                :key="skill.slug"
                class="flex items-start justify-between gap-4 py-4"
            >
                <div class="flex min-w-0 flex-col gap-1.5">
                    <div class="flex flex-wrap items-center gap-2">
                        <span class="font-medium">{{ skill.name }}</span>
                        <span
                            v-if="skill.excluded"
                            class="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                        >
                            Off for you
                        </span>
                    </div>
                    <p class="text-sm text-muted-foreground">
                        {{ skill.description }}
                    </p>
                    <p class="font-mono text-xs text-placeholder">
                        updated {{ formatDate(skill.updated_at) }}
                        <template v-if="skill.excluded">
                            · applies from your next message
                        </template>
                    </p>
                    <SkillCompatibilityNotice
                        class="mt-1"
                        :issues="skill.compatibility_issues"
                    />
                </div>

                <div class="flex shrink-0 items-center gap-2">
                    <Switch
                        :model-value="!skill.excluded"
                        :disabled="busySlugs.has(skill.slug)"
                        @update:model-value="
                            (enabled) => toggle(skill, enabled)
                        "
                    />
                    <Button
                        v-if="isAdmin"
                        variant="ghost"
                        size="icon"
                        class="text-muted-foreground hover:text-destructive"
                        :disabled="busySlugs.has(skill.slug)"
                        aria-label="Remove skill"
                        @click="askRemove(skill)"
                    >
                        <Trash2 class="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>

        <div
            v-if="isAdmin && skills.length && !isLoading"
            class="flex gap-2.5 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground"
        >
            <Info class="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>
                To correct a skill, import the file again under the same name.
                That keeps everyone's choices — removing it and importing it
                back would switch it on again for members who had switched it
                off.
            </p>
        </div>

        <ConfirmDialog
            v-model:isConfirmDialogOpen="isReplaceDialogOpen"
            :title="`Replace ${conflictName}?`"
            description="Your organisation already has a skill under that name. Replacing keeps everyone's on/off choices, but the previous version is gone for good."
            confirmButtonText="Replace"
            @confirm="replaceConflict"
        />

        <ConfirmDialog
            v-model:isConfirmDialogOpen="isDeleteDialogOpen"
            :title="`Remove ${skillToDelete?.name ?? ''}?`"
            description="Everyone in the organisation loses it. Importing it back switches it on again for members who had switched it off."
            confirmButtonText="Remove"
            @confirm="remove"
        />
    </div>
</template>
